/**
 * Admin Alert API Service
 *
 * API client for admin platform-wide alert visibility and management
 *
 * @module services/adminAlertApi
 */

import api from './api';

/**
 * Get platform-wide alert list with filtering
 *
 * @param {Object} filters - Filter parameters
 * @param {string} [filters.status] - Alert status
 * @param {string} [filters.priority] - Alert priority
 * @param {string} [filters.crisisType] - Crisis type
 * @param {string} [filters.source] - Alert source
 * @param {string} [filters.serviceGroupId] - Service Group ID
 * @param {string} [filters.therapistId] - Therapist ID
 * @param {string} [filters.supervisorId] - Clinical Supervisor ID
 * @param {string} [filters.slaStatus] - SLA status (breached, warning, on_track)
 * @param {string} [filters.dateFrom] - Start date (ISO string)
 * @param {string} [filters.dateTo] - End date (ISO string)
 * @param {number} [filters.limit] - Results per page (default 50)
 * @param {number} [filters.offset] - Pagination offset (default 0)
 * @param {string} [filters.sortBy] - Sort field (default 'createdAt')
 * @param {number} [filters.sortOrder] - Sort order: 1 (asc) | -1 (desc)
 * @returns {Promise<Object>} Alert list with pagination info
 */
export const getAlerts = async (filters = {}) => {
  try {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();

    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });

    const url = `/admin/alerts?${params.toString()}`;
    const response = await api.get(url, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching alerts:', error);
    throw error;
  }
};

/**
 * Get alert statistics with aggregation
 *
 * @param {Object} params - Query parameters
 * @param {string} [params.groupBy] - Group by: priority, crisisType, serviceGroup, therapist, source
 * @param {string} [params.dateFrom] - Start date (ISO string)
 * @param {string} [params.dateTo] - End date (ISO string)
 * @param {string} [params.status] - Filter by status
 * @returns {Promise<Object>} Statistics data
 */
export const getAlertStatistics = async (params = {}) => {
  try {
    const token = localStorage.getItem('token');
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const response = await api.get(`/admin/alerts/statistics?${queryParams.toString()}`, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching statistics:', error);
    throw error;
  }
};

/**
 * Get alerts approaching or breaching SLA deadlines
 *
 * @param {number} [warningThresholdMinutes=15] - Warning threshold in minutes
 * @returns {Promise<Object>} SLA status data with breached, approaching, and onTrack arrays
 */
export const getSLAStatus = async (warningThresholdMinutes = 15) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/admin/alerts/sla-status?warningThresholdMinutes=${warningThresholdMinutes}`, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching SLA status:', error);
    throw error;
  }
};

/**
 * Get alert breakdown by Service Group
 *
 * @returns {Promise<Object>} Service Group alert data
 */
export const getAlertsByServiceGroup = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/admin/alerts/service-groups`, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching service group alerts:', error);
    throw error;
  }
};

/**
 * Get alert breakdown by therapist with response time metrics
 *
 * @returns {Promise<Object>} Therapist alert data
 */
export const getAlertsByTherapist = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/admin/alerts/therapists`, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching therapist alerts:', error);
    throw error;
  }
};

/**
 * Get alert breakdown by Clinical Supervisor
 *
 * @returns {Promise<Object>} Clinical Supervisor alert data
 */
export const getAlertsBySupervisor = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/admin/alerts/supervisors`, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching supervisor alerts:', error);
    throw error;
  }
};

/**
 * Get alert volume trends over time
 *
 * @param {Object} params - Query parameters
 * @param {string} [params.period] - Period: daily, weekly, monthly
 * @param {string} [params.dateFrom] - Start date (ISO string)
 * @param {string} [params.dateTo] - End date (ISO string)
 * @returns {Promise<Object>} Trend data
 */
export const getAlertTrends = async (params = {}) => {
  try {
    const token = localStorage.getItem('token');
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });

    const response = await api.get(`/admin/alerts/trends?${queryParams.toString()}`, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching alert trends:', error);
    throw error;
  }
};

/**
 * Get detailed alert information
 *
 * @param {string} alertId - Alert ID
 * @returns {Promise<Object>} Detailed alert data
 */
export const getAlertDetails = async (alertId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await api.get(`/admin/alerts/${alertId}`, token);
    return response.data;
  } catch (error) {
    console.error('[AdminAlertApi] Error fetching alert details:', error);
    throw error;
  }
};

// Export all functions as default for convenience
export default {
  getAlerts,
  getAlertStatistics,
  getSLAStatus,
  getAlertsByServiceGroup,
  getAlertsByTherapist,
  getAlertsBySupervisor,
  getAlertTrends,
  getAlertDetails
};
